import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useProviderAuth } from '@/hooks/auth'
import { ApiError } from '@/lib/api'
import { config } from '@/lib/config'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const DEV_MOCK_USERS = [
  { email: 'provider@example.com', label: 'Dr. Sarah Chen', description: 'Full queue' },
  { email: 'empty@example.com', label: 'Dr. James Wilson', description: 'Empty queue' },
] as const

const loginSchema = z.object({
  email_or_username: z.string().min(1, 'Email or username is required'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginSchema>

export default function LoginPage() {
  const { login, isAuthenticated, isLoading } = useProviderAuth()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const location = useLocation()

  const from = (location.state as { from?: string })?.from || '/visits'

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  const onSubmit = async (data: LoginForm) => {
    try {
      setError('')
      await login(data.email_or_username, data.password)
      navigate(from)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Login failed. Please try again.')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-display font-semibold text-primary-600">Determa</h1>
          <p className="mt-1 text-sm text-muted-foreground">Provider Portal</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold">Sign in</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email_or_username">Email or Username</Label>
                <Input
                  id="email_or_username"
                  type="text"
                  autoComplete="username"
                  placeholder="you@example.com"
                  {...register('email_or_username')}
                />
                {errors.email_or_username && (
                  <p className="text-sm text-red-600">{errors.email_or_username.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  {...register('password')}
                />
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
          <CardFooter>
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-primary-600 hover:underline">
                Create one
              </Link>
            </p>
          </CardFooter>
        </Card>

        {config.enableMocks && (
          <div className="fixed bottom-4 right-4 w-64 rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 shadow-lg">
            <p className="mb-2 text-xs font-medium text-amber-700">Dev Mock Users (password: Password1)</p>
            <div className="space-y-1">
              {DEV_MOCK_USERS.map((user) => (
                <button
                  key={user.email}
                  type="button"
                  className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs hover:bg-amber-100"
                  onClick={() => {
                    setValue('email_or_username', user.email, { shouldValidate: true })
                    setValue('password', 'Password1', { shouldValidate: true })
                  }}
                >
                  <span className="font-medium text-amber-900">{user.label}</span>
                  <span className="text-amber-600">{user.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
